import { CommonModule, DatePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { ExportJobsService, PersistentExportJob } from './export-jobs.service';

@Component({
    selector: 'export-jobs-widget',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
    providers: [DatePipe],
    template: `
        @if (jobs.length > 0) {
            <div
                class="fixed right-6 top-20 z-50 flex max-w-[calc(100vw-3rem)] flex-col items-end gap-2"
            >
                <button
                    mat-stroked-button
                    class="rounded-xl border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900"
                    (click)="togglePanel()"
                    matTooltip="Show export jobs"
                >
                    <div class="flex items-center gap-2">
                        @if (hasActiveJobs) {
                            <mat-icon
                                svgIcon="heroicons_outline:arrow-path"
                                class="animate-spin text-blue-600"
                            ></mat-icon>
                        } @else {
                            <mat-icon
                                svgIcon="heroicons_outline:arrow-down-tray"
                                class="text-green-600"
                            ></mat-icon>
                        }
                        <span class="text-sm font-medium">{{
                            hasActiveJobs ? 'Exporting' : 'Export ready'
                        }}</span>
                        <span
                            class="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-white"
                        >
                            {{ jobs.length }}
                        </span>
                    </div>
                </button>

                @if (isOpen) {
                    <div
                        class="w-[min(560px,calc(100vw-3rem))] overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 shadow-xl dark:border-gray-700 dark:bg-gray-900"
                    >
                        <div
                            class="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900"
                        >
                            <div class="flex items-center gap-2">
                                <mat-icon
                                    svgIcon="heroicons_outline:arrow-down-tray"
                                    class="text-indigo-600 icon-size-5"
                                ></mat-icon>
                                <span
                                    class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                                    >Export Jobs</span
                                >
                            </div>
                            <div class="flex items-center gap-1">
                                <button
                                    mat-icon-button
                                    class="text-gray-400 hover:text-red-500"
                                    (click)="clearJobs()"
                                    matTooltip="Clear all jobs"
                                >
                                    <mat-icon
                                        svgIcon="heroicons_outline:trash"
                                        class="icon-size-4"
                                    ></mat-icon>
                                </button>
                                <button
                                    mat-icon-button
                                    class="text-gray-400"
                                    (click)="togglePanel()"
                                    matTooltip="Close"
                                >
                                    <mat-icon
                                        svgIcon="heroicons_outline:x-mark"
                                        class="icon-size-4"
                                    ></mat-icon>
                                </button>
                            </div>
                        </div>

                        <div
                            class="max-h-[50vh] divide-y divide-gray-100 overflow-y-auto dark:divide-gray-800"
                        >
                            @for (
                                job of jobs;
                                track job.kind + ':' + job.jobId
                            ) {
                                <div
                                    class="flex items-center gap-3 bg-white px-4 py-3 dark:bg-gray-900"
                                >
                                    <div class="min-w-0 flex-1">
                                        <div
                                            class="flex flex-wrap items-center gap-2"
                                        >
                                            <span
                                                class="truncate text-sm font-medium text-gray-800 dark:text-gray-100"
                                                >{{ job.label }}</span
                                            >
                                            <span
                                                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                                [ngClass]="
                                                    getStatusClass(job.status)
                                                "
                                            >
                                                {{ getStatusLabel(job.status) }}
                                            </span>
                                        </div>
                                        <div
                                            class="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-gray-400"
                                        >
                                            <span
                                                >Job ID:
                                                <span class="font-mono"
                                                    >{{
                                                        job.jobId
                                                            | slice: 0 : 8
                                                    }}...</span
                                                ></span
                                            >
                                            <span
                                                >Queued
                                                {{
                                                    job.createdAt
                                                        | date: 'HH:mm:ss'
                                                }}</span
                                            >
                                            @if (job.completedAt) {
                                                <span
                                                    >Done
                                                    {{
                                                        job.completedAt
                                                            | date: 'HH:mm:ss'
                                                    }}</span
                                                >
                                            }
                                        </div>
                                        @if (
                                            job.status === 'FAILED' &&
                                            job.errorMessage
                                        ) {
                                            <p
                                                class="mt-1 truncate text-xs text-red-500"
                                            >
                                                {{ job.errorMessage }}
                                            </p>
                                        }
                                    </div>

                                    <div
                                        class="flex flex-shrink-0 items-center gap-1"
                                    >
                                        @if (job.status === 'READY') {
                                            <button
                                                mat-flat-button
                                                color="primary"
                                                class="h-8 min-h-8 rounded-xl text-xs"
                                                (click)="downloadJob(job)"
                                            >
                                                <div
                                                    class="flex items-center gap-1"
                                                >
                                                    <mat-icon
                                                        svgIcon="heroicons_outline:arrow-down-tray"
                                                        class="icon-size-4"
                                                    ></mat-icon>
                                                    <span>Download</span>
                                                </div>
                                            </button>
                                        } @else if (job._pollTimedOut) {
                                            <button
                                                mat-stroked-button
                                                class="h-8 min-h-8 rounded-xl text-xs"
                                                (click)="restartJob(job)"
                                            >
                                                Resume
                                            </button>
                                        } @else if (
                                            job.status === 'PENDING' ||
                                            job.status === 'PROCESSING'
                                        ) {
                                            <mat-icon
                                                svgIcon="heroicons_outline:arrow-path"
                                                class="animate-spin text-blue-600 icon-size-5"
                                            ></mat-icon>
                                        }

                                        <button
                                            mat-icon-button
                                            class="text-gray-400 hover:text-red-500"
                                            (click)="removeJob(job)"
                                            matTooltip="Remove this job"
                                        >
                                            <mat-icon
                                                svgIcon="heroicons_outline:trash"
                                                class="icon-size-4"
                                            ></mat-icon>
                                        </button>
                                    </div>
                                </div>
                            }
                        </div>

                        <div
                            class="border-t border-gray-100 bg-gray-50 px-4 py-2 dark:border-gray-800 dark:bg-gray-900"
                        >
                            <p class="text-center text-xs text-gray-400">
                                Jobs are saved for this browser tab and polling
                                resumes after navigation or reload.
                            </p>
                        </div>
                    </div>
                }
            </div>
        }
    `,
})
export class ExportJobsWidgetComponent implements OnInit, OnDestroy {
    private readonly _jobsService = inject(ExportJobsService);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _datePipe = inject(DatePipe);
    private readonly _unsubscribeAll = new Subject<void>();

    jobs: PersistentExportJob[] = [];
    isOpen = false;

    get hasActiveJobs(): boolean {
        return this.jobs.some(
            (job) => job.status === 'PENDING' || job.status === 'PROCESSING'
        );
    }

    ngOnInit(): void {
        this._jobsService.jobs$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((jobs) => {
                this.jobs = jobs;
                if (jobs.length === 0) {
                    this.isOpen = false;
                }
                this._changeDetectorRef.markForCheck();
            });

        this._jobsService.panelOpen$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((isOpen) => {
                this.isOpen = isOpen;
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    togglePanel(): void {
        this._jobsService.togglePanel();
    }

    restartJob(job: PersistentExportJob): void {
        this._jobsService.restart(job);
    }

    downloadJob(job: PersistentExportJob): void {
        if (job.status !== 'READY') return;

        this._jobsService.download(job).subscribe((blob) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const dateStr = this._datePipe.transform(
                new Date(),
                'yyyy-MM-dd_HHmmss'
            );
            const ext = job.kind === 'incentive-statement' ? 'pdf' : 'xlsx';
            link.download = `${job.filenamePrefix}_${dateStr}.${ext}`;
            link.click();
            window.URL.revokeObjectURL(url);
        });
    }

    clearJobs(): void {
        this._jobsService.clear();
    }

    removeJob(job: PersistentExportJob): void {
        this._jobsService.remove(job);
    }

    getStatusLabel(status: string): string {
        switch (status) {
            case 'PENDING':
                return 'Pending';
            case 'PROCESSING':
                return 'Processing...';
            case 'READY':
                return 'Ready';
            case 'FAILED':
                return 'Failed';
            default:
                return status;
        }
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'PENDING':
                return 'bg-gray-100 text-gray-700';
            case 'PROCESSING':
                return 'bg-blue-100 text-blue-700';
            case 'READY':
                return 'bg-green-100 text-green-700';
            case 'FAILED':
                return 'bg-red-100 text-red-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    }
}
